import { useEffect, useState, useCallback, useMemo } from "react";
import { appendUnique, firstPage, listedRows, nextPage, trimPage } from "../../lib/paging";
import { LoadMore } from "./LoadMore";
import { Link, useNavigate } from "react-router";
import { Card, EmptyState, Button, Cluster, Stack, useToast } from "@salesapp/ui";
import { crmApi, type Order } from "../../lib/crmApi";
import { opsApi } from "../../lib/opsApi";
import { ACTION_HINTS, FIELD_HINTS, ORDER_STATUS_OPTIONS } from "../../help/fieldHints";
import { ListWorkspace, DeleteRecordButton } from "./RecordWorkspace";
import { SearchBar, StatusSlicer, useSelectOptions } from "./SearchBar";
import { StatusBadge } from "./StatusBadge";
import { useCrmHref } from "../../lib/customerUrl";
import { isAgedToHistory } from "../../lib/localeFormat";
import { useLocale } from "../../shell/LocaleProvider";
import { ResponsiveDataTable, type DataColumn, type DataRow } from "./ResponsiveDataTable";

function orderLocked(status: string | null | undefined): boolean {
  return Boolean(status) && status !== "pending";
}

const orderColumns: DataColumn[] = [
  { key: "number", label: "Order #", primary: true, hint: FIELD_HINTS.order_number },
  { key: "quote", label: "Quote #", primary: true, hint: "The quote this order was created from." },
  { key: "company", label: "Company", primary: true, hint: FIELD_HINTS.opportunity_company },
  { key: "status", label: "Status", primary: true, hint: FIELD_HINTS.order_status },
  { key: "total", label: "Total", primary: true, hint: "Order total in the order currency." },
  { key: "currency", label: "Currency", hint: "Currency code for this order’s amounts." },
  { key: "placed", label: "Placed at", hint: "When this order was created from an accepted quote." },
];

const historyColumns: DataColumn[] = [
  ...orderColumns.filter((c) => c.key !== "currency"),
  { key: "invoice", label: "Invoice", primary: true, hint: "The invoice generated from this order." },
];

const ORDER_STATUSES = ["pending", "confirmed", "fulfilled", "cancelled"];

export function OrdersPage({ orgId }: { orgId: string }) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const { show } = useToast();
  const navigate = useNavigate();
  const crmHref = useCrmHref();
  const { formatMoney, formatDateTime, historyAfterDays } = useLocale();
  const statuses = useSelectOptions(orgId, "order", "order_status", ORDER_STATUSES);

  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const listed = listedRows(await crmApi.listOrders(orgId, statusFilter || undefined, search || undefined, nextPage(orders.length), { withTotal: true }));
      const page = trimPage(listed.rows);
      setOrders((current) => appendUnique(current, page.items));
      setHasMore(page.hasMore);
      setTotal(listed.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load more orders");
    } finally {
      setLoadingMore(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const listed = listedRows(await crmApi.listOrders(orgId, statusFilter || undefined, search || undefined, firstPage(), { withTotal: true }));
      const page = trimPage(listed.rows);
      setOrders(page.items);
      setHasMore(page.hasMore);
      setTotal(listed.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [orgId, statusFilter, search]);

  useEffect(() => { void load(); }, [load]);

  // An invoiced order can't be invoiced again. It stays in the open list (greyed out) for the org's
  // "move to history after N days" setting, then drops into History.
  const isHistory = useCallback(
    (o: Order) => Boolean(o.invoice_id) && isAgedToHistory(o.invoice_issued_at, historyAfterDays),
    [historyAfterDays],
  );
  const openOrders = useMemo(() => orders.filter((o) => !isHistory(o)), [orders, isHistory]);
  const historyOrders = useMemo(() => orders.filter(isHistory), [orders, isHistory]);

  const baseCells = (o: Order) => ({
    number: <Link to={crmHref(`/orders/${o.id}`)}>{o.order_number}</Link>,
    quote: o.quote_id && o.quote_number ? <Link to={crmHref(`/quotes/${o.quote_id}`)}>{o.quote_number}</Link> : "—",
    company: o.company_id && o.company_name ? <Link to={crmHref(`/companies/${o.company_id}`)}>{o.company_name}</Link> : (o.company_name ?? "—"),
    status: <StatusBadge status={o.order_status} options={ORDER_STATUS_OPTIONS} />,
    total: o.total != null ? formatMoney(o.total, o.currency) : "—",
    currency: o.currency ?? "—",
    placed: o.placed_at ? formatDateTime(o.placed_at) : "—",
  });

  const buildHistoryRow = (o: Order): DataRow => ({
    id: o.id,
    href: crmHref(`/orders/${o.id}`),
    cells: {
      ...baseCells(o),
      invoice: o.invoice_id ? <Link to={crmHref(`/invoices/${o.invoice_id}`)}>{o.invoice_number}</Link> : "—",
    },
    actions: (
      <Button variant="ghost" title={ACTION_HINTS.view_order} onClick={() => navigate(crmHref(`/orders/${o.id}`))}>
        View
      </Button>
    ),
  });

  const buildRow = (o: Order): DataRow => {
    const locked = orderLocked(o.order_status);
    return {
      id: o.id,
      href: crmHref(`/orders/${o.id}`),
      cells: baseCells(o),
      actions: (
        <Cluster gap={2}>
          {o.order_status === "fulfilled" && o.invoice_id && (
            <Button
              variant="secondary"
              size="sm"
              disabled
              title={`Invoice ${o.invoice_number ?? ""} has already been generated from this order.`}
            >
              Invoiced
            </Button>
          )}
          {o.order_status === "fulfilled" && !o.invoice_id && (
            <Button
              variant="secondary"
              size="sm"
              title="Create an invoice from this fulfilled order."
              onClick={async () => {
                try {
                  await opsApi.createInvoice(orgId, o.id);
                  show("Invoice ready", "success");
                  await load();
                } catch (e) {
                  setError(e instanceof Error ? e.message : "Invoice could not be generated");
                }
              }}
            >
              Generate invoice
            </Button>
          )}
          <Button
            variant="ghost"
            title={locked ? ACTION_HINTS.view_order : ACTION_HINTS.edit_order}
            onClick={() => navigate(crmHref(`/orders/${o.id}`))}
          >
            {locked ? "View" : "Edit"}
          </Button>
          <DeleteRecordButton
            capability="crm.orders"
            recordLabel={o.order_number ?? "order"}
            size="sm"
            variant="ghost"
            onDeleted={async () => {
              try {
                await crmApi.deleteOrder(o.id, orgId);
                await load();
              } catch (e) {
                setError(e instanceof Error ? e.message : "Failed to delete order");
              }
            }}
          />
        </Cluster>
      ),
    };
  };

  return (
    <ListWorkspace
      title="Orders"
      toolbar={
        <div className="crm-toolbar">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search orders…"
            label="Search orders"
            hint="Search by order number, quote number, company, status, or notes."
          />
          <StatusSlicer
            value={statusFilter}
            onChange={setStatusFilter}
            options={statuses}
            optionHints={ORDER_STATUS_OPTIONS}
            title={ACTION_HINTS.filter_order_status}
          />
        </div>
      }
    >
      {error && <Card className="crm-error">{error}</Card>}
      {loading ? (
        <Card>Loading...</Card>
      ) : orders.length === 0 ? (
        <Card><EmptyState title="No orders found" description="Orders are generated from accepted quotes." /></Card>
      ) : (
        <Card className="crm-table-card">
          <ResponsiveDataTable caption="Open orders" columns={orderColumns} rows={openOrders.map(buildRow)} />
        </Card>
      )}
      {!loading && historyOrders.length > 0 && (
        <Stack gap={2}>
          <h2 className="crm-section-title">Order history</h2>
          <p className="crm-help">Orders that were invoiced more than {historyAfterDays} day(s) ago.</p>
          <Card className="crm-table-card">
            <ResponsiveDataTable caption="Order history" columns={historyColumns} rows={historyOrders.map(buildHistoryRow)} />
          </Card>
        </Stack>
      )}
      <LoadMore shown={orders.length} total={total} hasMore={hasMore} loading={loadingMore} onLoadMore={() => void loadMore()} noun="orders" />
    </ListWorkspace>
  );
}
